/**
 * Theme Presets — Modern Enterprise Design System
 *
 * Refined preset themes with cohesive color palettes.
 * All presets meet WCAG AA contrast ratios.
 */

const PRESETS = {
  default: {
    id: 'default',
    name: 'Professional Blue',
    description: 'Clean blue primary with white surfaces — modern and trustworthy',
    preview: { primary: '#2563eb', secondary: '#7c3aed', accent: '#0284c7' },
    light: {
      primaryColor: '#2563eb',
      secondaryColor: '#7c3aed',
      accentColor: '#0284c7',
    },
    dark: {
      primaryColor: '#3b82f6',
      secondaryColor: '#a78bfa',
      accentColor: '#38bdf8',
    },
  },
  enterpriseDark: {
    id: 'enterpriseDark',
    name: 'Enterprise Dark',
    description: 'Comfortable dark mode for long working sessions',
    preview: { primary: '#3b82f6', secondary: '#8b5cf6', accent: '#06b6d4' },
    light: {
      primaryColor: '#2563eb',
      secondaryColor: '#7c3aed',
      accentColor: '#0891b2',
    },
    dark: {
      primaryColor: '#60a5fa',
      secondaryColor: '#a78bfa',
      accentColor: '#22d3ee',
    },
  },
  midnightBlue: {
    id: 'midnightBlue',
    name: 'Midnight',
    description: 'Deep navy with blue accents for focused environments',
    preview: { primary: '#3b82f6', secondary: '#6366f1', accent: '#06b6d4' },
    light: {
      primaryColor: '#2563eb',
      secondaryColor: '#4f46e5',
      accentColor: '#0284c7',
    },
    dark: {
      primaryColor: '#60a5fa',
      secondaryColor: '#818cf8',
      accentColor: '#67e8f9',
    },
  },
  forest: {
    id: 'forest',
    name: 'Forest',
    description: 'Professional green tones for infrastructure teams',
    preview: { primary: '#059669', secondary: '#0d9488', accent: '#047857' },
    light: {
      primaryColor: '#059669',
      secondaryColor: '#0d9488',
      accentColor: '#047857',
    },
    dark: {
      primaryColor: '#34d399',
      secondaryColor: '#2dd4bf',
      accentColor: '#6ee7b7',
    },
  },
  executiveBlack: {
    id: 'executiveBlack',
    name: 'Executive',
    description: 'Premium dark with gold highlights',
    preview: { primary: '#d97706', secondary: '#b45309', accent: '#f59e0b' },
    light: {
      primaryColor: '#1e1e1e',
      secondaryColor: '#b45309',
      accentColor: '#d97706',
    },
    dark: {
      primaryColor: '#fbbf24',
      secondaryColor: '#f59e0b',
      accentColor: '#fcd34d',
    },
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean',
    description: 'Calming teal and cyan tones',
    preview: { primary: '#0d9488', secondary: '#0891b2', accent: '#06b6d4' },
    light: {
      primaryColor: '#0d9488',
      secondaryColor: '#0891b2',
      accentColor: '#06b6d4',
    },
    dark: {
      primaryColor: '#2dd4bf',
      secondaryColor: '#22d3ee',
      accentColor: '#67e8f9',
    },
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset',
    description: 'Warm orange and amber tones',
    preview: { primary: '#ea580c', secondary: '#d97706', accent: '#b45309' },
    light: {
      primaryColor: '#ea580c',
      secondaryColor: '#d97706',
      accentColor: '#b45309',
    },
    dark: {
      primaryColor: '#fb923c',
      secondaryColor: '#fbbf24',
      accentColor: '#fcd34d',
    },
  },
  royal: {
    id: 'royal',
    name: 'Royal',
    description: 'Rich purple and indigo accents',
    preview: { primary: '#7c3aed', secondary: '#6366f1', accent: '#4f46e5' },
    light: {
      primaryColor: '#7c3aed',
      secondaryColor: '#6366f1',
      accentColor: '#4f46e5',
    },
    dark: {
      primaryColor: '#a78bfa',
      secondaryColor: '#818cf8',
      accentColor: '#a5b4fc',
    },
  },
  rose: {
    id: 'rose',
    name: 'Rose',
    description: 'Soft pink and rose tones',
    preview: { primary: '#e11d48', secondary: '#be123c', accent: '#f43f5e' },
    light: {
      primaryColor: '#e11d48',
      secondaryColor: '#be123c',
      accentColor: '#f43f5e',
    },
    dark: {
      primaryColor: '#fb7185',
      secondaryColor: '#fda4af',
      accentColor: '#f43f5e',
    },
  },
  slate: {
    id: 'slate',
    name: 'Minimal',
    description: 'Monochromatic slate for reduced visual noise',
    preview: { primary: '#475569', secondary: '#64748b', accent: '#334155' },
    light: {
      primaryColor: '#475569',
      secondaryColor: '#64748b',
      accentColor: '#334155',
    },
    dark: {
      primaryColor: '#94a3b8',
      secondaryColor: '#cbd5e1',
      accentColor: '#e2e8f0',
    },
  },
  highContrast: {
    id: 'highContrast',
    name: 'High Contrast',
    description: 'WCAG AAA compliant with maximum contrast',
    preview: { primary: '#000000', secondary: '#1e1e1e', accent: '#0000cc' },
    light: {
      primaryColor: '#000000',
      secondaryColor: '#1e1e1e',
      accentColor: '#0000cc',
    },
    dark: {
      primaryColor: '#ffffff',
      secondaryColor: '#f0f0f0',
      accentColor: '#66b3ff',
    },
  },

  // ─── New presets ──────────────────────────────────────────────────────────
  teal: {
    id: 'teal',
    name: 'Aqua',
    description: 'Fresh teal and sky tones for a calm, modern workspace',
    preview: { primary: '#0891b2', secondary: '#0ea5e9', accent: '#14b8a6' },
    light: {
      primaryColor: '#0891b2',
      secondaryColor: '#0284c7',
      accentColor: '#0d9488',
    },
    dark: {
      primaryColor: '#22d3ee',
      secondaryColor: '#38bdf8',
      accentColor: '#2dd4bf',
    },
  },
  emerald: {
    id: 'emerald',
    name: 'Emerald',
    description: 'Vibrant emerald with lime accents — energetic and lively',
    preview: { primary: '#10b981', secondary: '#22c55e', accent: '#84cc16' },
    light: {
      primaryColor: '#059669',
      secondaryColor: '#16a34a',
      accentColor: '#65a30d',
    },
    dark: {
      primaryColor: '#34d399',
      secondaryColor: '#4ade80',
      accentColor: '#a3e635',
    },
  },
  grape: {
    id: 'grape',
    name: 'Grape',
    description: 'Playful violet and fuchsia for a bold, creative feel',
    preview: { primary: '#9333ea', secondary: '#c026d3', accent: '#d946ef' },
    light: {
      primaryColor: '#9333ea',
      secondaryColor: '#c026d3',
      accentColor: '#a21caf',
    },
    dark: {
      primaryColor: '#c084fc',
      secondaryColor: '#e879f9',
      accentColor: '#f0abfc',
    },
  },
  coral: {
    id: 'coral',
    name: 'Coral',
    description: 'Warm coral and pink — friendly and welcoming',
    preview: { primary: '#f43f5e', secondary: '#fb7185', accent: '#f97316' },
    light: {
      primaryColor: '#e11d48',
      secondaryColor: '#f43f5e',
      accentColor: '#ea580c',
    },
    dark: {
      primaryColor: '#fb7185',
      secondaryColor: '#fda4af',
      accentColor: '#fdba74',
    },
  },
  indigo: {
    id: 'indigo',
    name: 'Indigo',
    description: 'Deep indigo with soft blue accents — focused and refined',
    preview: { primary: '#4f46e5', secondary: '#6366f1', accent: '#3b82f6' },
    light: {
      primaryColor: '#4338ca',
      secondaryColor: '#4f46e5',
      accentColor: '#2563eb',
    },
    dark: {
      primaryColor: '#818cf8',
      secondaryColor: '#a5b4fc',
      accentColor: '#93c5fd',
    },
  },
  amberGold: {
    id: 'amberGold',
    name: 'Amber',
    description: 'Warm amber and honey tones — bright and optimistic',
    preview: { primary: '#d97706', secondary: '#f59e0b', accent: '#eab308' },
    light: {
      primaryColor: '#b45309',
      secondaryColor: '#d97706',
      accentColor: '#ca8a04',
    },
    dark: {
      primaryColor: '#fbbf24',
      secondaryColor: '#fcd34d',
      accentColor: '#fde047',
    },
  },
  crimson: {
    id: 'crimson',
    name: 'Crimson',
    description: 'Confident red with warm accents — strong and decisive',
    preview: { primary: '#dc2626', secondary: '#ef4444', accent: '#f97316' },
    light: {
      primaryColor: '#b91c1c',
      secondaryColor: '#dc2626',
      accentColor: '#ea580c',
    },
    dark: {
      primaryColor: '#f87171',
      secondaryColor: '#fca5a5',
      accentColor: '#fdba74',
    },
  },
  nordic: {
    id: 'nordic',
    name: 'Nordic',
    description: 'Cool steel-blue with frost accents — clean and minimal',
    preview: { primary: '#3b82f6', secondary: '#64748b', accent: '#38bdf8' },
    light: {
      primaryColor: '#2563eb',
      secondaryColor: '#475569',
      accentColor: '#0ea5e9',
    },
    dark: {
      primaryColor: '#60a5fa',
      secondaryColor: '#94a3b8',
      accentColor: '#7dd3fc',
    },
  },

  // ─── Extra vivid presets (distinct hues) ────────────────────────────────────
  mango: {
    id: 'mango',
    name: 'Mango',
    description: 'Juicy orange with lime and yellow — bright and cheerful',
    preview: { primary: '#f97316', secondary: '#84cc16', accent: '#facc15' },
    light: {
      primaryColor: '#ea580c',
      secondaryColor: '#65a30d',
      accentColor: '#ca8a04',
    },
    dark: {
      primaryColor: '#fb923c',
      secondaryColor: '#a3e635',
      accentColor: '#fde047',
    },
  },
  bubblegum: {
    id: 'bubblegum',
    name: 'Bubblegum',
    description: 'Playful pink and purple — fun and modern',
    preview: { primary: '#ec4899', secondary: '#a855f7', accent: '#f472b6' },
    light: {
      primaryColor: '#db2777',
      secondaryColor: '#9333ea',
      accentColor: '#e11d48',
    },
    dark: {
      primaryColor: '#f472b6',
      secondaryColor: '#c084fc',
      accentColor: '#fb7185',
    },
  },
  turquoise: {
    id: 'turquoise',
    name: 'Turquoise',
    description: 'Bright turquoise with coral pop — energetic and fresh',
    preview: { primary: '#06b6d4', secondary: '#14b8a6', accent: '#fb7185' },
    light: {
      primaryColor: '#0891b2',
      secondaryColor: '#0d9488',
      accentColor: '#f43f5e',
    },
    dark: {
      primaryColor: '#22d3ee',
      secondaryColor: '#2dd4bf',
      accentColor: '#fb7185',
    },
  },
  lavender: {
    id: 'lavender',
    name: 'Lavender',
    description: 'Soft violet with pink and blue accents — calm and elegant',
    preview: { primary: '#8b5cf6', secondary: '#ec4899', accent: '#60a5fa' },
    light: {
      primaryColor: '#7c3aed',
      secondaryColor: '#db2777',
      accentColor: '#2563eb',
    },
    dark: {
      primaryColor: '#a78bfa',
      secondaryColor: '#f472b6',
      accentColor: '#93c5fd',
    },
  },
  lime: {
    id: 'lime',
    name: 'Lime',
    description: 'Zesty lime-green with teal — vibrant and outdoorsy',
    preview: { primary: '#65a30d', secondary: '#0d9488', accent: '#84cc16' },
    light: {
      primaryColor: '#4d7c0f',
      secondaryColor: '#0f766e',
      accentColor: '#65a30d',
    },
    dark: {
      primaryColor: '#a3e635',
      secondaryColor: '#2dd4bf',
      accentColor: '#bef264',
    },
  },
  cherry: {
    id: 'cherry',
    name: 'Cherry',
    description: 'Deep cherry-red with pink and amber — warm and bold',
    preview: { primary: '#be123c', secondary: '#e11d48', accent: '#f59e0b' },
    light: {
      primaryColor: '#9f1239',
      secondaryColor: '#be123c',
      accentColor: '#d97706',
    },
    dark: {
      primaryColor: '#fb7185',
      secondaryColor: '#fda4af',
      accentColor: '#fbbf24',
    },
  },
  cocoa: {
    id: 'cocoa',
    name: 'Cocoa',
    description: 'Warm brown and tan tones — earthy and comfortable',
    preview: { primary: '#92400e', secondary: '#b45309', accent: '#d97706' },
    light: {
      primaryColor: '#78350f',
      secondaryColor: '#92400e',
      accentColor: '#b45309',
    },
    dark: {
      primaryColor: '#d97706',
      secondaryColor: '#f59e0b',
      accentColor: '#fcd34d',
    },
  },
  peacock: {
    id: 'peacock',
    name: 'Peacock',
    description: 'Rich teal-blue with green and gold — luxurious and vivid',
    preview: { primary: '#0e7490', secondary: '#047857', accent: '#eab308' },
    light: {
      primaryColor: '#155e75',
      secondaryColor: '#047857',
      accentColor: '#ca8a04',
    },
    dark: {
      primaryColor: '#22d3ee',
      secondaryColor: '#34d399',
      accentColor: '#fde047',
    },
  },
  graphite: {
    id: 'graphite',
    name: 'Graphite',
    description: 'Charcoal grey with blue accent — sleek and understated',
    preview: { primary: '#334155', secondary: '#1e293b', accent: '#3b82f6' },
    light: {
      primaryColor: '#1e293b',
      secondaryColor: '#334155',
      accentColor: '#2563eb',
    },
    dark: {
      primaryColor: '#cbd5e1',
      secondaryColor: '#94a3b8',
      accentColor: '#60a5fa',
    },
  },
};

export default PRESETS;
export const PRESET_LIST = Object.values(PRESETS);
